
import React, { useState, useRef, useEffect } from 'react';
import { queryAgent } from '../services/gemini';
import { AgentModule, AgentSettings } from '../types';

interface AIAgentProps {
  context: string;
  agentSettings: AgentSettings;
  module?: AgentModule;
}

const AIAgent: React.FC<AIAgentProps> = ({ context, agentSettings, module }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [input, setInput] = useState('');
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [messages, setMessages] = useState<{role: 'user' | 'agent', text: string, image?: string}[]>([
    {
      role: 'agent',
      text: module === 'WHATSAPP'
        ? 'WhatsApp Copilot online. I can review draft replies, improve tone, summarize threads, and help retrieve quote or estimate context.'
        : "Operational Assistant online. I can review tasks, explain SOP steps, and flag operational risks. How can I help?"
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedImage(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSend = async () => {
    if ((!input.trim() && !selectedImage) || isLoading) return;
    
    const userMessage = input || (selectedImage ? "[Attached Image Analysis]" : "");
    const currentImage = selectedImage;
    
    setInput('');
    setSelectedImage(null);
    setMessages(prev => [...prev, { role: 'user', text: userMessage, image: currentImage || undefined }]);
    setIsLoading(true);

    const response = await queryAgent(userMessage, context, currentImage || undefined, 'OPERATIONAL', module);
    
    setMessages(prev => [...prev, { role: 'agent', text: response || "System connectivity issue detected. Please retry request." }]);
    setIsLoading(false);
  };

  return (
    <div className={`fixed bottom-10 right-10 flex flex-col items-end gap-6 z-50 transition-all duration-500 ${isOpen ? 'w-[450px] h-[750px]' : 'w-20 h-20'}`}>
      {isOpen ? (
        <div className="w-full h-full bg-white/95 backdrop-blur-3xl rounded-[2.5rem] shadow-[0_30px_90px_rgba(0,0,0,0.25)] border border-white flex flex-col overflow-hidden animate-in zoom-in-95 duration-300">
          <header className="p-8 bg-slate-900 text-white flex justify-between items-center shrink-0">
            <div className="flex items-center gap-4">
                <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_10px_rgba(16,185,129,0.8)]"></div>
                <div className="flex flex-col">
                  <h3 className="font-black text-sm tracking-tight">Vision Intelligence</h3>
                  <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest">
                    {agentSettings.provider.type === 'NONE' ? 'Rules Only' : `${agentSettings.provider.type} Enabled`}
                  </span>
                </div>
            </div>
            <button onClick={() => setIsOpen(false)} className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center text-white/40 hover:text-white transition-all">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </header>

          <div ref={scrollRef} className="flex-1 overflow-y-auto p-8 space-y-8 custom-scrollbar bg-slate-50/50">
            {messages.map((m, i) => (
              <div key={i} className={`flex flex-col ${m.role === 'user' ? 'items-end' : 'items-start'}`}>
                {m.image && (
                  <div className="max-w-[80%] mb-2 rounded-2xl overflow-hidden shadow-lg border-4 border-white">
                    <img src={m.image} alt="User Upload" className="w-full h-auto" />
                  </div>
                )}
                <div className={`max-w-[85%] p-6 rounded-[1.8rem] text-sm font-medium leading-relaxed ${
                    m.role === 'user' 
                        ? 'bg-indigo-600 text-white rounded-tr-none shadow-xl shadow-indigo-100' 
                        : 'bg-white text-slate-700 rounded-tl-none border border-slate-100 shadow-sm'
                }`}>
                    {m.text}
                </div>
              </div>
            ))}
            {isLoading && (
                <div className="flex justify-start">
                    <div className="bg-white p-6 rounded-[1.8rem] rounded-tl-none border border-slate-100 text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center gap-4">
                        <div className="flex gap-1.5">
                            <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce"></span>
                            <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.2s]"></span>
                            <span className="w-1.5 h-1.5 bg-indigo-600 rounded-full animate-bounce [animation-delay:0.4s]"></span>
                        </div>
                        Processing Multi-modal Data
                    </div>
                </div>
            )}
          </div>

          <div className="p-8 bg-white border-t border-slate-50 space-y-4">
            {selectedImage && (
              <div className="relative w-24 h-24 rounded-2xl overflow-hidden border-2 border-indigo-500 shadow-lg group">
                <img src={selectedImage} alt="Preview" className="w-full h-full object-cover" />
                <button 
                  onClick={() => setSelectedImage(null)}
                  className="absolute inset-0 bg-black/40 flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M6 18L18 6M6 6l12 12" /></svg>
                </button>
              </div>
            )}
            
            <div className="flex items-center gap-4">
              <button 
                onClick={() => fileInputRef.current?.click()}
                className="w-14 h-14 bg-slate-50 rounded-2xl flex items-center justify-center text-slate-400 hover:bg-slate-900 hover:text-white transition-all shadow-sm border border-slate-100 shrink-0"
                title="Attach Document or Image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
              </button>
              <input type="file" ref={fileInputRef} onChange={handleImageUpload} accept="image/*" className="hidden" />
              
              <div className="flex-1 relative">
                <input 
                  type="text"
                  placeholder="Ask about mission, document or photo..."
                  value={input}
                  onChange={e => setInput(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSend()}
                  className="w-full bg-slate-50 border-none rounded-2xl px-6 py-4 pr-16 text-sm font-bold text-slate-800 placeholder-slate-300 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                />
                <button 
                  onClick={handleSend}
                  disabled={isLoading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 bg-indigo-600 rounded-xl flex items-center justify-center text-white hover:bg-indigo-700 transition-all disabled:opacity-50"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" /></svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <button 
          onClick={() => setIsOpen(true)}
          className="w-20 h-20 bg-slate-900 rounded-[2rem] shadow-[0_15px_45px_rgba(0,0,0,0.2)] flex items-center justify-center text-white hover:scale-110 hover:bg-indigo-600 transition-all duration-500 group"
        >
          <div className="relative">
             <svg className="w-10 h-10 group-hover:scale-110 transition-transform duration-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" /></svg>
             <div className="absolute -top-1 -right-1 w-4 h-4 bg-indigo-500 rounded-full border-2 border-slate-900 animate-pulse"></div>
          </div>
        </button>
      )}
    </div>
  );
};

export default AIAgent;
